import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const OB_STATUSES = [
  'active',
  'day30_pending_approval',
  'day60_pending_approval',
  'day90_pending_approval',
  'completed',
  'extended',
  'cancelled',
];
export const OB_PERIODS = ['day_1_30', 'day_31_60', 'day_61_90', 'extension'];
export const OB_SOURCES = ['pb_handover', 'ats_direct', 'standalone'];
export const OB_RISK_LEVELS = ['green', 'yellow', 'red', 'insufficient_data'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const boolFilter = (label, t) => ({
  filter: true, sort: false, filterType: 'custom',
  filterOptions: {
    defaultOp: 'eq',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0] !== undefined ? filterList[index][0] : '';
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              filterList[index] = v === '' ? [] : [v];
              onChange(filterList[index], index, column);
            }}
          >
            <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
            <MenuItem value="true">{t('common.yes')}</MenuItem>
            <MenuItem value="false">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>
      );
    },
  },
});

const useObOnboardingsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/onboardings?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    try {
      const payload = {
        employeeId: formData.employeeId,
        managerEmployeeId: formData.managerEmployeeId,
        startDate: formData.startDate,
        buddyEmployeeId: formData.buddyEmployeeId || null,
        templateId: formData.templateId || null,
        source: 'standalone',
      };
      await api.post('/v1/onboardings', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const extend = useCallback(async (id, body) => {
    try { await api.post(`/v1/onboardings/${id}/extend`, body); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id, reason) => {
    try { await api.post(`/v1/onboardings/${id}/cancel`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const close = useCallback(async (id, body) => {
    try { await api.post(`/v1/onboardings/${id}/close`, body || {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const requestApproval = useCallback(async (onboardingId, period, note) => {
    try { await api.post(`/v1/onboarding-period-approvals/request`, { onboardingId, period, notesMarkdown: note }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const statusLabel = (s) => t(`onboarding.status.${s}`, { defaultValue: s });
    const periodLabel = (s) => t(`onboarding.period.${s}`, { defaultValue: s });
    const sourceLabel = (s) => t(`onboarding.source.${s}`, { defaultValue: s });
    const riskLabel = (s) => t(`onboarding.riskLevel.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('onboarding.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'personEmail',
        label: t('onboarding.email'),
        options: {
          filter: true, sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personEmail[like]' },
        },
      },
      {
        name: 'status',
        label: t('onboarding.statusLabel'),
        options: {
          ...multiSelectFilter(t('onboarding.statusLabel'), OB_STATUSES, statusLabel),
          // Under a "…Onayı Bekliyor" status, surface WHICH gate the process waits
          // on and WHOSE action it needs (from the backend-derived pending fields),
          // so the row answers "who is it pending on" at a glance.
          customBodyRender: (v, tableMeta) => {
            const base = statusLabel(v);
            const row = data[tableMeta.rowIndex];
            const stage = row && row.pendingApprovalStage;
            if (!stage) return base;
            const stageText = t(`onboarding.pendingStage.${stage}`, { defaultValue: '' });
            const who = row.pendingApproverName;
            return (
              <div>
                <div>{base}</div>
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
                  {who ? `${stageText}: ${who}` : stageText}
                </div>
              </div>
            );
          },
        },
      },
      {
        name: 'currentPeriod',
        label: t('onboarding.periodLabel'),
        options: {
          ...multiSelectFilter(t('onboarding.periodLabel'), OB_PERIODS, periodLabel),
          customBodyRender: (v) => periodLabel(v),
        },
      },
      {
        name: 'source',
        label: t('onboarding.sourceLabel'),
        options: {
          ...multiSelectFilter(t('onboarding.sourceLabel'), OB_SOURCES, sourceLabel),
          customBodyRender: (v) => sourceLabel(v),
        },
      },
      {
        name: 'earlyLeaveRiskLevel',
        label: t('onboarding.riskLabel'),
        options: {
          ...multiSelectFilter(t('onboarding.riskLabel'), OB_RISK_LEVELS, riskLabel),
          customBodyRender: (v) => v ? riskLabel(v) : '-',
        },
      },
      dateFilterColumn('startDate', t('onboarding.startDate'), lang),
      dateFilterColumn('expectedDay90', t('onboarding.expectedDay90'), lang),
      numericFilterColumn('npsScoreLatest', t('onboarding.npsScoreLatest'), { decimals: 0 }),
      {
        name: 'buddyEmployeeId',
        label: t('onboarding.buddyAssigned'),
        options: { ...boolFilter(t('onboarding.buddyAssigned'), t), customBodyRender: (v) => v ? '✓' : '-' },
      },
    ];
  }, [t, lang, data]);

  const employeeAsync = useMemo(() => ({
    onSearch: async (q) => {
      const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
    },
    onGetById: async (id) => {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data || {};
      return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture };
    },
  }), []);

  // Onboarding tracks the first 90 days — the subject picker only offers
  // recent hires (employment started at most 90 days ago, or in the future
  // for handed-over preboardings). Manager/buddy stay unrestricted.
  const newHireAsync = useMemo(() => ({
    onSearch: async (q) => {
      const windowStart = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const params = { 'employmentDate[gte]': windowStart, pageSize: 20 };
      if (q) params['firstName[like]'] = q;
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
    },
    onGetById: employeeAsync.onGetById,
  }), [employeeAsync]);

  const objectSchema = useMemo(() => ([
    {
      name: 'employeeId', label: t('onboarding.form.employee'), type: 'asyncSelect', grid: 6, required: true,
      ...newHireAsync,
      deriveFields: async (employeeId) => {
        if (!employeeId) return null;
        // Always reset _employmentDate so switching to another employee never leaves a
        // stale value behind (which would mis-fire the start-date warning). null means
        // "unknown" → handleSave's guard simply skips the warning.
        const patch = { _employmentDate: null };
        try {
          const mgr = await api.get(`/v1/employees/${employeeId}/manager`);
          const m = mgr.data?.data;
          if (m?.employeeId) patch.managerEmployeeId = m.employeeId;
        } catch { /* leave manager untouched on failure */ }
        try {
          // Stash the employee's actual employment date so handleSave can soft-warn
          // when the chosen onboarding start date differs from it (non-blocking).
          const emp = await api.get(`/v1/employees/${employeeId}`);
          const ed = emp.data?.data?.employmentDate;
          if (ed) patch._employmentDate = String(ed).split('T')[0];
        } catch { /* employmentDate is best-effort for the soft warning */ }
        return patch;
      },
    },
    { name: 'managerEmployeeId', label: t('onboarding.form.manager'), type: 'asyncSelect', grid: 6, required: true, ...employeeAsync },
    { name: 'startDate', label: t('onboarding.form.startDate'), type: 'date', grid: 6, required: true },
    { name: 'buddyEmployeeId', label: t('onboarding.form.buddy'), type: 'asyncSelect', grid: 6, ...employeeAsync },
    {
      name: 'templateId', label: t('onboarding.form.template'), type: 'asyncSelect', grid: 6,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/onboarding-task-templates', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((tp) => ({ id: tp.id, name: tp.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/onboarding-task-templates/${id}`);
        const tp = res.data?.data || {};
        return { id, name: tp.name || '' };
      },
    },
  ]), [t, employeeAsync, newHireAsync]);

  return { fetching, data, pagination, columns, objectSchema, refresh, create, extend, cancel, close, requestApproval };
};

export default useObOnboardingsApi;
